import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";

const EFFECTIVE_DATE = new Date().toLocaleDateString("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const SECTIONS = [
  {
    heading: "Information We Collect",
    body: (
      <>
        <p>We may collect the following information when you interact with our website:</p>
        <p className="mt-4 font-medium text-stone-900 uppercase tracking-[0.18em] text-[11px]">Personal Information</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Full Name</li>
          <li>Email Address</li>
          <li>Phone Number</li>
          <li>Company Name (if applicable)</li>
          <li>Project Location</li>
          <li>Any information you voluntarily provide through contact forms or inquiries</li>
        </ul>
        <p className="mt-4 font-medium text-stone-900 uppercase tracking-[0.18em] text-[11px]">Technical Information</p>
        <p className="mt-2">We may automatically collect:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>IP address</li>
          <li>Browser type and version</li>
          <li>Device information</li>
          <li>Operating system</li>
          <li>Pages visited</li>
          <li>Date and time of visit</li>
          <li>Referring website</li>
          <li>Cookies and similar technologies</li>
        </ul>
      </>
    ),
  },
  {
    heading: "How We Use Your Information",
    body: (
      <>
        <p>We use your information to:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Respond to your inquiries</li>
          <li>Provide quotations and consultations</li>
          <li>Communicate regarding projects and services</li>
          <li>Improve our website and user experience</li>
          <li>Send project-related updates</li>
          <li>Maintain customer records</li>
          <li>Comply with applicable legal obligations</li>
        </ul>
        <p className="mt-4 italic text-stone-900">We do not sell or rent your personal information to third parties.</p>
      </>
    ),
  },
  {
    heading: "Custom Design Confidentiality",
    body: (
      <>
        <p>Our company specializes in creating exclusive custom surface designs.</p>
        <p className="mt-3">
          Any design concepts, project details, or visual references shared with us during consultations are treated with
          appropriate confidentiality and used solely for project-related purposes unless otherwise agreed.
        </p>
      </>
    ),
  },
  {
    heading: "Cookies",
    body: (
      <>
        <p>Our website may use cookies to:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Improve website functionality</li>
          <li>Analyze website traffic</li>
          <li>Remember user preferences</li>
          <li>Enhance browsing experience</li>
        </ul>
        <p className="mt-3">
          You may disable cookies through your browser settings, although some website features may not function properly.
        </p>
      </>
    ),
  },
  {
    heading: "Third-Party Services",
    body: (
      <>
        <p>Our website may use trusted third-party providers for services such as:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Website analytics</li>
          <li>Contact forms</li>
          <li>Email communication</li>
          <li>Cloud hosting</li>
          <li>Embedded maps</li>
          <li>Social media integrations</li>
        </ul>
        <p className="mt-3">These providers may process information only as necessary to perform their services.</p>
      </>
    ),
  },
  {
    heading: "Data Security",
    body: (
      <>
        <p>
          We implement reasonable administrative, technical, and organizational measures to protect your information against
          unauthorized access, alteration, disclosure, or destruction.
        </p>
        <p className="mt-3">
          While we strive to protect your data, no method of electronic transmission or storage can be guaranteed to be
          completely secure.
        </p>
      </>
    ),
  },
  {
    heading: "Data Retention",
    body: (
      <>
        <p>We retain personal information only for as long as necessary to:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Provide requested services</li>
          <li>Fulfill contractual obligations</li>
          <li>Meet legal and regulatory requirements</li>
          <li>Resolve disputes</li>
        </ul>
        <p className="mt-3">Information that is no longer required will be securely deleted or anonymized where appropriate.</p>
      </>
    ),
  },
  {
    heading: "Your Rights",
    body: (
      <>
        <p>Depending on your applicable local laws, you may have the right to:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Access your personal information</li>
          <li>Request correction of inaccurate information</li>
          <li>Request deletion of your personal data</li>
          <li>Withdraw consent where applicable</li>
          <li>Object to certain processing activities</li>
          <li>Request a copy of your personal information</li>
        </ul>
        <p className="mt-3">To exercise these rights, please contact us using the details below.</p>
      </>
    ),
  },
  {
    heading: "Children’s Privacy",
    body: (
      <p>
        Our website and services are intended for individuals who are at least 18 years of age. We do not knowingly collect
        personal information from children.
      </p>
    ),
  },
  {
    heading: "External Links",
    body: (
      <p>
        Our website may contain links to external websites. We are not responsible for the privacy practices or content of
        third-party websites. We encourage you to review their privacy policies before providing personal information.
      </p>
    ),
  },
  {
    heading: "Updates to This Privacy Policy",
    body: (
      <p>
        We may update this Privacy Policy periodically to reflect changes in our services, legal requirements, or business
        practices. Any updates will be posted on this page with the revised effective date.
      </p>
    ),
  },
];

export default function PrivacyPolicyDialog({ trigger, className = "" }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button
            data-testid="privacy-policy-trigger"
            className={className || "link-underline text-sm text-stone-200 hover:text-white"}
          >
            Privacy Policy
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        data-testid="privacy-policy-dialog"
        className="max-w-3xl bg-white p-0 rounded-sm border border-stone-200 overflow-hidden"
      >
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-stone-200">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#4A6076] mb-2">Legal — VR Custom Solutions</p>
          <DialogTitle className="font-serif-display text-3xl md:text-4xl text-stone-900 tracking-tight">
            Privacy Policy
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500 mt-1">
            Effective Date: {EFFECTIVE_DATE}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[70vh] px-8 pb-8">
          <div className="prose prose-stone max-w-none text-[14.5px] leading-relaxed text-stone-700 pt-6 space-y-6">
            <p>
              Welcome to <span className="font-medium text-stone-900">VR Custom Solutions</span>. We value your privacy and are
              committed to protecting the personal information you share with us. This Privacy Policy explains how we collect,
              use, store, and safeguard your information when you visit our website or contact us regarding our custom surface
              design services.
            </p>
            <p>By using our website, you agree to the practices described in this Privacy Policy.</p>

            {SECTIONS.map((s) => (
              <section key={s.heading} className="pt-4 border-t border-stone-100">
                <h3 className="font-serif-display text-xl text-stone-900 mb-3">{s.heading}</h3>
                <div className="text-stone-600">{s.body}</div>
              </section>
            ))}

            <section className="pt-4 border-t border-stone-100">
              <h3 className="font-serif-display text-xl text-stone-900 mb-3">Contact</h3>
              <p>Questions about this Privacy Policy? Reach us at</p>
              <p className="mt-2 text-stone-900">
                <a href="mailto:admin@varnella.in" className="link-underline">admin@varnella.in</a>
                &nbsp;·&nbsp;
                <a href="tel:+919566413125" className="link-underline">+91 95664 13125</a>
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
